import React, { useState } from 'react';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { motion } from 'framer-motion';
import { 
  TrendingUp, Users, Target, Calendar, Filter,
  ArrowUpRight, ArrowDownRight, DollarSign
} from 'lucide-react';

import { salesTrends, performanceRankings } from '../data/mockData';

const PerformanceTab = ({ timeRange }) => {
  const [chartType, setChartType] = useState('sales');
  const [period, setPeriod] = useState('monthly');
  const [chartMode, setChartMode] = useState('composed'); // 'composed' or 'single'
  const [selectedMetrics, setSelectedMetrics] = useState(['sales', 'transactions']);

  const metrics = [
    { id: 'sales', label: 'Sales', color: '#3B82F6' },
    { id: 'transactions', label: 'Transactions', color: '#10B981' },
    { id: 'avgTicket', label: 'Average Ticket', color: '#8B5CF6' }
  ];

  const getChartData = () => {
    switch (period) {
      case 'daily':
        return salesTrends.daily.map(item => ({
          ...item,
          date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        }));
      case 'weekly':
        return salesTrends.weekly.map(item => ({
          ...item,
          date: `Week ${item.week}`
        }));
      case 'monthly':
      default:
        return salesTrends.monthly;
    }
  };

  const formatYAxis = (value) => {
    if (value >= 1000000000) {
      return `${(value / 1000000000).toFixed(1)}B`;
    }
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value;
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Analysis</h2>
            
            <div className="flex items-center gap-2">
              {chartMode === 'single' ? (
                // Single metric selection buttons
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setChartType('sales')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                      chartType === 'sales' 
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' 
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    Sales
                  </button>
                  <button
                    onClick={() => setChartType('transactions')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                      chartType === 'transactions' 
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400' 
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    Transactions
                  </button>
                  <button
                    onClick={() => setChartType('avgTicket')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                      chartType === 'avgTicket' 
                        ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400' 
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    Avg Ticket
                  </button>
                </div>
              ) : (
                // Multiple metrics toggles
                <div className="flex items-center gap-2">
                  {metrics.map(metric => (
                    <button
                      key={metric.id}
                      onClick={() => {
                        setSelectedMetrics(current => 
                          current.includes(metric.id)
                            ? current.filter(m => m !== metric.id)
                            : [...current, metric.id]
                        );
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                        selectedMetrics.includes(metric.id)
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {metric.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="rounded-lg border border-gray-200 dark:border-gray-700 text-sm p-2 bg-white dark:bg-gray-800"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            
            <select
              value={chartMode}
              onChange={(e) => setChartMode(e.target.value)}
              className="rounded-lg border border-gray-200 dark:border-gray-700 text-sm p-2 bg-white dark:bg-gray-800"
            >
              <option value="composed">Multi-Metric</option>
              <option value="single">Single Metric</option>
            </select>
            
            <button className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg">
              <Filter className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          {chartMode === 'single' 
            ? (chartType === 'sales' ? 'Sales Performance' : 
              chartType === 'transactions' ? 'Transaction Volume' : 'Average Ticket Size')
            : 'Performance Metrics Comparison'
          }
        </h2>
        
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            {chartMode === 'composed' ? (
              // Composed Chart (Multi-metric)
              <ComposedChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey={period === 'monthly' ? 'month' : 'date'} />
                <YAxis 
                  yAxisId="left" 
                  tickFormatter={formatYAxis}
                  domain={['auto', 'auto']}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right"
                  tickFormatter={formatYAxis}
                  domain={['auto', 'auto']}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'sales') return [`${formatYAxis(value)} SAR`, 'Sales'];
                    if (name === 'transactions') return [value.toLocaleString(), 'Transactions'];
                    if (name === 'avgTicket') return [`${value.toLocaleString()} SAR`, 'Avg Ticket'];
                    return [value, name];
                  }}
                />
                <Legend />
                
                {selectedMetrics.includes('sales') && (
                  <Bar
                    yAxisId="left"
                    dataKey="sales"
                    name="Sales"
                    fill="#3B82F6"
                    opacity={0.8}
                    radius={[4, 4, 0, 0]}
                  />
                )}
                
                {selectedMetrics.includes('transactions') && (
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="transactions"
                    name="Transactions"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                )}
                
                {selectedMetrics.includes('avgTicket') && (
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="avgTicket"
                    name="Avg Ticket"
                    stroke="#8B5CF6"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                )}
              </ComposedChart>
            ) : (
              // Single Metric Chart
              chartType === 'avgTicket' ? (
                <LineChart data={getChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={period === 'monthly' ? 'month' : 'date'} />
                  <YAxis 
                    tickFormatter={formatYAxis}
                    domain={['auto', 'auto']}
                  />
                  <Tooltip 
                    formatter={(value) => [
                      `SAR ${value.toLocaleString()}`,
                      chartType === 'sales' ? 'Sales' : 
                      chartType === 'transactions' ? 'Transactions' : 'Avg Ticket'
                    ]}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey={chartType} 
                    stroke="#8B5CF6" 
                    strokeWidth={2}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              ) : (
                <BarChart data={getChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={period === 'monthly' ? 'month' : 'date'} />
                  <YAxis 
                    tickFormatter={formatYAxis}
                    domain={['auto', 'auto']}
                  />
                  <Tooltip 
                    formatter={(value) => [
                      chartType === 'sales' 
                        ? `SAR ${value.toLocaleString()}` 
                        : value.toLocaleString(),
                      chartType === 'sales' ? 'Sales' : 
                      chartType === 'transactions' ? 'Transactions' : 'Avg Ticket'
                    ]}
                  />
                  <Legend />
                  <Bar 
                    dataKey={chartType} 
                    fill={
                      chartType === 'sales' ? '#3B82F6' : 
                      chartType === 'transactions' ? '#10B981' : '#8B5CF6'
                    } 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              )
            )}
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                <DollarSign className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Sales</h3>
                <p className="text-xl font-semibold text-gray-900 dark:text-white">SAR 45.8M</p>
              </div>
            </div>
            <div className="flex items-center text-sm text-green-600 dark:text-green-400">
              <ArrowUpRight className="w-4 h-4 mr-1" />
              <span>12.5%</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20">
                <Users className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Transactions</h3>
                <p className="text-xl font-semibold text-gray-900 dark:text-white">356,429</p>
              </div>
            </div>
            <div className="flex items-center text-sm text-green-600 dark:text-green-400">
              <ArrowUpRight className="w-4 h-4 mr-1" />
              <span>8.3%</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                <Target className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Ticket</h3>
                <p className="text-xl font-semibold text-gray-900 dark:text-white">SAR 128</p>
              </div>
            </div>
            <div className="flex items-center text-sm text-green-600 dark:text-green-400">
              <ArrowUpRight className="w-4 h-4 mr-1" />
              <span>3.7%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceTab; 

//